// src/store/modules/mediaActions.js
import {
  collection,
  getDocs,
  getDoc,
  doc,
  addDoc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch,
  getStorage,
  storageRef,
  deleteObject,
} from "@/services/legacyBrowserPersistenceBoundary";
import {
  isDualWriteEnabled,
  isTimelineOnlyEnabled,
  isTimelineDebugEnabled,
} from "@/config/timeline";
import { createMedia, updateMedia, deleteMedia } from "@/utils/timelineRepo";
import {
  deleteStorageFiles,
  cleanupChangedFiles,
} from "@/utils/storageCleanup";
import { deriveTextualMediaType } from "@/utils/textualMediaType";
import {
  assetIdForAuthorityUrl,
  rememberAuthorityAssetUrl,
} from "@/services/authorityAssetUrlRegistry";

const AUTHORITY_ASSET_FIELDS = Object.freeze([
  "images", "mediaThumbnails", "originalMediaThumbnails", "originalVideoUrls",
  "documentUrls", "mediaUrl", "audioUrl", "videoUrl", "processedVideoUrl",
  "originalVideoUrl", "thumbnailUrl",
]);

function bindAuthorityAssets(payload) {
  const media = { ...(payload || {}) };
  const assetIds = { ...(payload?.assetIds || {}) };
  for (const field of AUTHORITY_ASSET_FIELDS) {
    const value = media[field];
    if (Array.isArray(value)) {
      const ids = value.map(assetIdForAuthorityUrl);
      if (ids.some(Boolean)) {
        if (ids.some((id) => !id)) throw new Error("Media assets must use one storage authority.");
        assetIds[field] = ids;
        media[field] = [];
      }
    } else if (typeof value === "string") {
      const assetId = assetIdForAuthorityUrl(value);
      if (assetId) {
        assetIds[field] = assetId;
        media[field] = "";
      }
    }
  }
  if (Object.keys(assetIds).length > 0) media.assetIds = assetIds;
  else delete media.assetIds;
  return media;
}

const createMediaCreationOperationId = () => {
  const cryptoProvider = globalThis.crypto;
  if (typeof cryptoProvider?.randomUUID === "function") {
    const candidate = cryptoProvider.randomUUID().replaceAll("-", "_");
    if (/^[A-Za-z0-9_-]{16,128}$/.test(candidate)) return candidate;
  }
  if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    return `media_${Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("")}`;
  }
  throw new Error("Secure media operations are unavailable.");
};

/**
 * Media persistence rules reference (create/edit matrix):
 * docs/media_upload_schema.md#Create/Edit-Rules-Matrix
 *
 * Key points enforced here:
 * - Keep collection stable on edit (use original collection from caller).
 * - Persist images exactly as provided; do not rehydrate deletions.
 * - Preserve mediaUrl for videos; remove for non-video unless explicitly set.
 * - Clamp/normalize thumbnailIndex in UI layer; store as provided.
 */

export default {
  namespaced: true, // Make sure the module is namespaced
  state: {
    mediaPosts: [],
    loading: false,
    currentMedia: null,
  },
  getters: {
    mediaPosts: (state) => state.mediaPosts,
    mediaLoading: (state) => state.loading, // Getter for loading state if needed
    currentMedia: (state) => state.currentMedia,
  },
  actions: {
    // Set the current media for viewing in ProductView
    setCurrentMedia({ commit }, media) {
      console.log("Setting current media in store:", media);
      commit("SET_CURRENT_MEDIA", media);
    },

    // Clear the current media when no longer needed
    clearCurrentMedia({ commit }) {
      commit("CLEAR_CURRENT_MEDIA");
    },

    // Update existing media item
    async updateMediaItem(
      { commit, dispatch },
      { id, collection, data, existingUrls = [] }
    ) {
      commit("SET_LOADING", true);
      try {
        if (isTimelineDebugEnabled()) {
          console.log(`[MediaActions] Updating media item ${id}:`, data);
        }

        // Get current document for storage cleanup comparison
        let currentDoc = null;
        try {
          const { getById } = await import("@/utils/timelineRepo");
          currentDoc = await getById(id);
          if (isTimelineDebugEnabled()) {
            console.log(
              "[MediaActions] Current document for cleanup:",
              currentDoc
            );
          }
        } catch (error) {
          console.warn(
            "[MediaActions] Could not fetch current document for cleanup:",
            error
          );
        }

        // Prepare payload for timeline repository
        const timelinePayload = {
          ...data,
          images: existingUrls.length > 0 ? existingUrls : data.images || [],
        };

        // Update in timeline repository (primary)
        let timelineResult = null;
        try {
          timelineResult = await updateMedia(id, bindAuthorityAssets(timelinePayload));

          if (isTimelineDebugEnabled()) {
            console.log(
              "[MediaActions] ✅ Timeline update successful:",
              timelineResult
            );
          }
        } catch (timelineError) {
          console.error("[MediaActions] ❌ Authority update failed:", timelineError);
          throw timelineError;
        }

        // STORAGE CLEANUP: Remove orphaned files when editing
        if (currentDoc) {
          try {
            const cleanupResult = await cleanupChangedFiles(
              currentDoc,
              timelinePayload
            );
            if (cleanupResult.totalDeleted > 0) {
              console.log(
                `[MediaActions] ✅ Cleaned up ${cleanupResult.totalDeleted} orphaned files`
              );
            }
            if (cleanupResult.totalFailed > 0) {
              console.warn(
                `[MediaActions] ⚠️ Failed to clean up ${cleanupResult.totalFailed} files:`,
                cleanupResult.failures
              );
            }
          } catch (cleanupError) {
            console.warn(
              "[MediaActions] Storage cleanup failed:",
              cleanupError
            );
            // Don't fail the update if cleanup fails
          }
        }

        // Optional compatibility dual-write is intentionally disabled for the
        // authority path; browser legacy writes remain fail-closed.
        if (isDualWriteEnabled()) {
          try {
            const legacyResult = await dispatch("updateMediaItemLegacy", {
              id,
              collection,
              data,
              existingUrls,
            });

            if (isTimelineDebugEnabled()) {
              console.log(
                "[MediaActions] ✅ Legacy compatibility update successful:",
                legacyResult
              );
            }

          } catch (legacyError) {
            console.warn(
              "[MediaActions] ⚠️ Legacy update failed:",
              legacyError
            );
          }
        } else if (isTimelineDebugEnabled()) {
          console.log(
            "[MediaActions] 🎯 Timeline-only mode: skipping legacy update"
          );
        }

        return timelineResult;
      } catch (error) {
        console.error("Error updating media item:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Legacy update method (preserved for dual-write and fallback)
    async updateMediaItemLegacy(
      { commit },
      { id, collection, data, existingUrls = [] }
    ) {
      console.log(
        `Updating media item ${id} in collection ${collection}:`,
        data
      );

      // Create an array of all images
      let allImages = [];

      // Add the main URL if present — but NOT for video/audio posts,
      // where mediaUrl holds the media file, not an image.
      if (data.mediaUrl && data.mediaType !== "video" && data.mediaType !== "audio") {
        allImages.push(data.mediaUrl);
      }

      // Add any additional images from the data object
      if (data.additionalImages && data.additionalImages.length > 0) {
        data.additionalImages.forEach((url) => {
          if (!allImages.includes(url)) {
            allImages.push(url);
          }
        });
      }

      // Add any existing URLs not already included
      if (existingUrls && existingUrls.length > 0) {
        existingUrls.forEach((url) => {
          if (!allImages.includes(url)) {
            allImages.push(url);
          }
        });
      }

      // Remove duplicates
      allImages = [...new Set(allImages)];

      // Create updated data structure matching product schema
      const updatedData = {
        ...data,
        // Honor deletions: if no images remain, set to []
        images: Array.isArray(allImages) ? allImages : [],
        type: "media", // Add type field for consistency
        thumbnailIndex: data.thumbnailIndex || 0,
      };

      // If variants present on payload, persist them
      if (data.variants && typeof data.variants === "object") {
        updatedData.variants = data.variants;
      }

      // Remove legacy fields to standardize the schema
      delete updatedData.additionalImages;

      // CRITICAL: Preserve mediaUrl for video posts - this is the primary video URL.
      // For non-video, remove mediaUrl to standardize unless explicitly set (e.g., audio poster or legacy).
      if (updatedData.mediaType !== "video") {
        if (!("mediaUrl" in data)) {
          delete updatedData.mediaUrl;
        }
      }

      const docRef = doc(null, collection, id);
      await setDoc(docRef, updatedData, { merge: true });
      console.log(`Legacy media item ${id} updated successfully`);
      return { success: true, id };
    },

    // Upload media file to Storage and return URL
    async uploadMediaFile({ commit }, { file, type, owner, organisation, onProgress, creationOperationId, mediaId }) {
      commit("SET_LOADING", true);
      try {
        console.log("Uploading file to storage:", {
          file,
          type,
          owner,
          organisation,
        });

        // Handle non-File objects
        if (!(file instanceof File) && !(file instanceof Blob)) {
          if (typeof file === "string") {
            console.log("File is already a URL, not uploading:", file);
            return file; // Just return the URL as-is
          }
          console.error("Invalid file object, cannot upload:", file);
          throw new Error("Invalid file object for upload");
        }

        const resourceId = mediaId || (creationOperationId ? `media_${creationOperationId}` : null);
        if (!resourceId) throw new Error("A media post identity is required for upload.");
        const {
          describeStorageAuthorityFile,
          storageAuthorityService,
        } = await import("@/services/storageAuthorityService");
        const descriptor = await describeStorageAuthorityFile(file, "media-post");
        onProgress?.(0);
        const grant = await storageAuthorityService.createUploadIntent({
          operationId: createMediaCreationOperationId(),
          expectedRevision: 0,
          resource: { kind: "media-post", resourceId },
          audience: "private",
          file: descriptor,
        });
        await storageAuthorityService.putUploadFile(grant, file);
        const finalized = await storageAuthorityService.finalizeUpload({
          operationId: createMediaCreationOperationId(),
          expectedRevision: 0,
          uploadId: grant.uploadId,
        });
        const download = await storageAuthorityService.createAssetDownloadUrl(finalized.assetId);
        rememberAuthorityAssetUrl(download.downloadUrl, finalized.assetId);
        onProgress?.(100);
        return download.downloadUrl;
      } catch (error) {
        console.error("Error uploading media file:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    async addMediaPost({ commit, dispatch }, payload) {
      commit("SET_LOADING", true);
      try {
        if (isTimelineDebugEnabled()) {
          console.log("[MediaActions] Adding media post:", payload);
        }

        const {
          creationOperationId,
          ...mediaPayload
        } = payload || {};

        // A few compatibility callers predate the composer-owned operation
        // ID. Generate the same opaque, high-entropy control value here so all
        // media creates still receive the deterministic media_<operationId>
        // identity required by the private authority.
        const effectiveCreationOperationId =
          creationOperationId || createMediaCreationOperationId();

        // Timeline-first creation. The opaque operation ID is control data and
        // is never persisted as media content or copied to a legacy collection.
        const timelineResult = await createMedia(bindAuthorityAssets(mediaPayload), {
          operationId: effectiveCreationOperationId,
        });

        if (isTimelineDebugEnabled()) {
          console.log(
            "[MediaActions] ✅ Timeline creation successful:",
            timelineResult
          );
        }

        // DUAL-WRITE: Also write to legacy collections for safety (only if dual-write enabled)
        if (isDualWriteEnabled()) {
          try {
            const legacyResult = await dispatch(
              "addMediaPostLegacy",
              mediaPayload,
            );

            if (isTimelineDebugEnabled()) {
              console.log(
                "[MediaActions] ✅ Legacy dual-write successful:",
                legacyResult
              );
            }
          } catch (legacyError) {
            console.warn(
              "[MediaActions] ⚠️ Legacy dual-write failed (timeline succeeded):",
              legacyError
            );
            // Don't fail the operation if timeline succeeded but legacy failed
          }
        } else if (isTimelineDebugEnabled()) {
          console.log(
            "[MediaActions] 🎯 Timeline-only mode: skipping legacy write"
          );
        }

        return timelineResult;
      } catch (error) {
        console.error("[MediaActions] Error adding media post:", error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Legacy creation method (preserved for dual-write and fallback)
    async addMediaPostLegacy({ commit }, payload) {
      const {
        itemName,
        caption,
        images = [],
        mediaType = "image",
        owner,
        organisation,
      } = payload;

      // Detect document posts (new schema)
      const documentUrls = Array.isArray(payload.documentUrls)
        ? payload.documentUrls.filter((u) => typeof u === "string" && u)
        : [];
      const hasDocuments = documentUrls.length > 0;
      const documentMetadata = Array.isArray(payload.documentMetadata)
        ? payload.documentMetadata
        : [];

      // Derive textual media sub-types when there is no media file content
      let derivedMediaType = mediaType;
      const hasImages = Array.isArray(images) && images.length > 0;

      // Embed posts (YouTube, SoundCloud) keep their mediaType as-is — skip derivation
      const isEmbed = derivedMediaType === "embed" || !!payload.embedHtml;

      if (isEmbed) {
        derivedMediaType = "embed";
      } else if (hasDocuments) {
        derivedMediaType = "documents";
      } else if (!hasImages) {
        derivedMediaType = deriveTextualMediaType(itemName, caption);
      }

      // Determine collection based on media type
      let collectionName = "images";
      if (derivedMediaType === "video") {
        collectionName = "videos";
      } else if (derivedMediaType === "audio") {
        collectionName = "audios";
      } else if (
        ["poster", "tweet", "post", "documents", "embed"].includes(derivedMediaType)
      ) {
        collectionName = "images";
      }

      // Prepare the document data
      const mediaData = {
        itemName,
        caption,
        description: payload.description || "",
        imageCaptions: Array.isArray(payload.imageCaptions) ? payload.imageCaptions : [],
        images: images,
        mediaTypes: Array.isArray(payload.mediaTypes) ? payload.mediaTypes : [],
        mediaThumbnails: Array.isArray(payload.mediaThumbnails)
          ? payload.mediaThumbnails
          : [],
        mediaVariants: Array.isArray(payload.mediaVariants)
          ? payload.mediaVariants
          : [],
        mediaThumbnailVariants: Array.isArray(payload.mediaThumbnailVariants)
          ? payload.mediaThumbnailVariants
          : [],
        originalMediaThumbnails: Array.isArray(payload.originalMediaThumbnails)
          ? payload.originalMediaThumbnails
          : [],
        owner,
        organisation,
        createdAt: serverTimestamp(),
        mediaType: derivedMediaType,
        type: "media",
        thumbnailIndex: payload.thumbnailIndex || 0,
      };

      // Persist documents if present
      if (hasDocuments) {
        mediaData.documentUrls = documentUrls;
        if (documentMetadata.length)
          mediaData.documentMetadata = documentMetadata;
        if (!hasImages) mediaData.images = [];
      }

      // Persist responsive variants for the primary image if provided
      if (payload.variants && typeof payload.variants === "object") {
        mediaData.variants = payload.variants;
      }

      [
        "processedVideoUrl",
        "originalVideoUrl",
        "videoDeliveryProfile",
        "videoEncoderRevision",
      ].forEach(
        (field) => {
          if (payload[field]) mediaData[field] = payload[field];
        },
      );
      if (Array.isArray(payload.originalVideoUrls)) {
        mediaData.originalVideoUrls = payload.originalVideoUrls;
      }

      // Persist embed fields for embed posts (YouTube, SoundCloud)
      if (payload.embedHtml) {
        mediaData.embedHtml = payload.embedHtml;
        mediaData.embedUrl = payload.embedUrl || "";
        mediaData.embedProvider = payload.embedProvider || null;
        if (payload.embedIsVertical) mediaData.embedIsVertical = true;
        if (payload.embedIsSquare) mediaData.embedIsSquare = true;
        if (payload.thumbnailUrl) mediaData.thumbnailUrl = payload.thumbnailUrl;
      }

      const docRef = await addDoc(collection(null, collectionName), mediaData);
      console.log("Legacy media post added with ID:", docRef.id);
      return { success: true, id: docRef.id };
    },

    async fetchAllMediaPosts({ commit }) {
      commit("SET_LOADING", true);
      try {
        const imageSnap = await getDocs(collection(null, "images"));
        const videoSnap = await getDocs(collection(null, "videos"));
        const audioSnap = await getDocs(collection(null, "audios"));

        // Process images, ensuring they have the right fields
        const images = imageSnap.docs.map((doc) => {
          const data = doc.data();

          // Convert legacy structure to new structure if needed
          if (!data.images || data.images.length === 0) {
            // Handle legacy format with mediaUrl and additionalImages
            const allImages = [];

            if (data.mediaUrl) {
              allImages.push(data.mediaUrl);
            }

            if (data.additionalImages && Array.isArray(data.additionalImages)) {
              data.additionalImages.forEach((url) => {
                if (!allImages.includes(url)) {
                  allImages.push(url);
                }
              });
            }

            data.images = allImages;
            data.thumbnailIndex = 0;
          }

          return {
            id: doc.id,
            ...data,
            type: "media", // Add type field for consistency with products
          };
        });

        // Process videos
        const videos = videoSnap.docs.map((doc) => {
          const data = doc.data();
          // Add type for consistency
          return {
            id: doc.id,
            ...data,
            type: "media",
          };
        });

        // Process audios
        const audios = audioSnap.docs.map((doc) => {
          const data = doc.data();
          // Add type for consistency
          return {
            id: doc.id,
            ...data,
            type: "media",
          };
        });

        const combined = [...images, ...videos, ...audios];
        commit("SET_MEDIA_POSTS", combined);

        console.log(
          `Fetched ${combined.length} media posts with standardized format`
        );
      } catch (error) {
        console.error("Error fetching media posts:", error);
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Delete an item and its associated storage files
    async deleteItem(
      { commit, dispatch },
      { id, type, mediaType, images = [] }
    ) {
      commit("SET_LOADING", true);

      if (isTimelineDebugEnabled()) {
        console.log(
          `[MediaActions] Deleting item with dual-write: ID=${id}, Type=${type}, MediaType=${mediaType}`
        );
      }

      try {
        // PHASE 2: For media items, use timeline repository with enhanced storage cleanup
        if (type === "media") {
          try {
            // Delete from timeline repository (includes comprehensive storage cleanup)
            const timelineResult = await deleteMedia(id);

            if (isTimelineDebugEnabled()) {
              console.log(
                "[MediaActions] ✅ Timeline deletion successful:",
                timelineResult
              );
            }

            // DUAL-WRITE: Also delete from legacy collections for safety (only if dual-write enabled)
            if (isDualWriteEnabled()) {
              try {
                const legacyResult = await dispatch("deleteItemLegacy", {
                  id,
                  type,
                  mediaType,
                  images,
                });

                if (isTimelineDebugEnabled()) {
                  console.log(
                    "[MediaActions] ✅ Legacy dual-write deletion successful:",
                    legacyResult
                  );
                }
              } catch (legacyError) {
                console.warn(
                  "[MediaActions] ⚠️ Legacy dual-write deletion failed (timeline succeeded):",
                  legacyError
                );
                // Don't fail the operation if timeline succeeded but legacy failed
              }
            } else if (isTimelineDebugEnabled()) {
              console.log(
                "[MediaActions] 🎯 Timeline-only mode: skipping legacy deletion"
              );
            }

            return timelineResult;
          } catch (timelineError) {
            console.error(
              "[MediaActions] ❌ Authority deletion failed:",
              timelineError
            );
            throw timelineError;
          }
        } else {
          // For products/services, use legacy deletion (no timeline migration yet)
          return await dispatch("deleteItemLegacy", {
            id,
            type,
            mediaType,
            images,
          });
        }
      } catch (error) {
        console.error(`Error deleting item: ${id}`, error);
        throw error;
      } finally {
        commit("SET_LOADING", false);
      }
    },

    // Legacy deletion method (preserved for dual-write and non-media items)
    async deleteItemLegacy(
      { commit, dispatch },
      { id, type, mediaType, images = [] }
    ) {
      // Determine the correct collection based on the item type
      let parentCollection;
      let refreshAction;

      if (type === "product") {
        parentCollection = "products";
        refreshAction = async () => {
          await dispatch("fetchProducts", {
            batchSize: 10,
            refresh: true,
          });
        };
      } else if (type === "service") {
        parentCollection = "services";
        refreshAction = async () => {
          await dispatch("fetchServices", {
            batchSize: 10,
            refresh: true,
          });
        };
      } else if (type === "media") {
        // For media items, check the mediaType to determine the collection
        if (mediaType === "image") {
          parentCollection = "images";
        } else if (mediaType === "video") {
          parentCollection = "videos";
        } else if (mediaType === "audio") {
          parentCollection = "audios";
        } else {
          parentCollection = "images"; // Default to images if no specific type
        }

        refreshAction = async () => {
          await dispatch("fetchAllMediaPosts");
        };
      } else {
        throw new Error(`Unknown item type: ${type}`);
      }

      console.log(`Using collection: ${parentCollection} for item deletion`);

      // 1. Delete related images from storage if they exist
      if (images && images.length > 0) {
        await dispatch("deleteImagesFromStorage", images);
      }

      // 2. Delete subcollection if it's a service (like slots)
      if (type === "service") {
        await dispatch("deleteSubcollection", {
          parentCollection,
          parentId: id,
          subcollection: "slots",
        });
      }

      // 3. Delete the main document
      const docRef = doc(null, parentCollection, id);
      await deleteDoc(docRef);
      console.log(`Deleted document: ${parentCollection}/${id}`);

      // 4. Refresh the data
      if (refreshAction) {
        await refreshAction();
      }

      return { success: true };
    },

    // Helper action to delete images from Firebase Storage
    async deleteImagesFromStorage({ commit }, imageUrls) {
      if (!imageUrls || !imageUrls.length) return;

      const storage = getStorage();
      const deletePromises = imageUrls.map(async (url) => {
        if (!url || typeof url !== "string") return;

        try {
          // Extract the storage path from the URL
          // Firebase storage URLs are typically in the format:
          // https://firebasestorage.googleapis.com/v0/b/[bucket]/o/[encoded-path]?alt=media&token=[token]

          // Method 1: Direct reference if we can decode the URL
          try {
            const decodedUrl = decodeURIComponent(url);
            const baseUrl = `https://firebasestorage.googleapis.com/v0/b/${storage.app.options.storageBucket}/o/`;
            if (decodedUrl.startsWith(baseUrl)) {
              const path = decodedUrl.replace(baseUrl, "").split("?")[0];
              if (path) {
                const fileRef = storageRef(storage, path);
                await deleteObject(fileRef);
                console.log(
                  `Deleted image from storage: ${url} (path: ${path})`
                );
                return;
              }
            }
          } catch (decodeError) {
            console.warn(`Failed to decode URL ${url}: ${decodeError}`);
            // Continue to other methods
          }

          // Method 2: Try to extract path from URL structure
          try {
            // Extract path from URL after /o/ and before ?
            const pathMatch = url.match(/\/o\/([^?]+)/);
            if (pathMatch && pathMatch[1]) {
              const path = decodeURIComponent(pathMatch[1]);
              const fileRef = storageRef(storage, path);
              await deleteObject(fileRef);
              console.log(
                `Deleted image from storage using path extraction: ${url} (path: ${path})`
              );
              return;
            }
          } catch (extractError) {
            console.warn(
              `Failed to extract path from URL ${url}: ${extractError}`
            );
          }

          // Method 3: Last resort - try direct reference
          try {
            const fileRef = storageRef(storage, url);
            await deleteObject(fileRef);
            console.log(
              `Deleted image from storage using direct reference: ${url}`
            );
          } catch (directError) {
            console.error(
              `Failed to delete media from storage: ${url}`,
              directError
            );
          }
        } catch (error) {
          console.error(`Failed to delete media from storage: ${url}`, error);
        }
      });

      await Promise.all(deletePromises);
    },

    // Helper action to delete a subcollection
    async deleteSubcollection(
      { commit },
      { parentCollection, parentId, subcollection }
    ) {
      try {
        const subcollectionRef = collection(
          null,
          parentCollection,
          parentId,
          subcollection
        );
        const snapshot = await getDocs(subcollectionRef);

        if (snapshot.empty) {
          console.log(`No documents found in ${subcollection} subcollection.`);
          return;
        }

        const batch = writeBatch(null);
        snapshot.forEach((docSnap) => {
          batch.delete(docSnap.ref);
        });

        await batch.commit();
        console.log(
          `Deleted all documents in ${subcollection} subcollection for ${parentCollection}/${parentId}.`
        );
      } catch (error) {
        console.error(`Error deleting subcollection ${subcollection}:`, error);
        throw error;
      }
    },
  },
  mutations: {
    SET_LOADING(state, status) {
      state.loading = status;
    },
    SET_MEDIA_POSTS(state, posts) {
      state.mediaPosts = posts;
    },
    SET_CURRENT_MEDIA(state, media) {
      state.currentMedia = media;
    },
    CLEAR_CURRENT_MEDIA(state) {
      state.currentMedia = null;
    },

    // Optimistic update mutations
    ADD_OPTIMISTIC_MEDIA(state, item) {
      console.log("Adding optimistic media item to UI:", item);
      state.mediaPosts = [item, ...state.mediaPosts];
    },

    UPDATE_OPTIMISTIC_MEDIA(state, { tempId, realId }) {
      console.log(`Updating optimistic media item: ${tempId} -> ${realId}`);
      const index = state.mediaPosts.findIndex((item) => item.id === tempId);
      if (index !== -1) {
        state.mediaPosts[index].id = realId;
        state.mediaPosts[index].isOptimistic = false;
      }
    },

    REMOVE_OPTIMISTIC_MEDIA(state, id) {
      console.log(`Removing optimistic media item: ${id}`);
      state.mediaPosts = state.mediaPosts.filter((item) => item.id !== id);
    },
  },
};
